import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  Stack,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HistoryIcon from '@mui/icons-material/History';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { apiClient } from '../services/apiClient';

const TIER_CONFIG = {
  BRONZE: {
    label: 'Bronze Voucher',
    color: '#D69A6C',
    gradient: 'linear-gradient(135deg, #D69A6C 0%, #A9673A 100%)',
    bgLight: 'rgba(214, 154, 108, 0.12)',
    border: 'rgba(214, 154, 108, 0.3)',
  },
  SILVER: {
    label: 'Silver Voucher',
    color: '#D8DEDF',
    gradient: 'linear-gradient(135deg, #D8DEDF 0%, #8F9A9C 100%)',
    bgLight: 'rgba(216, 222, 223, 0.12)',
    border: 'rgba(216, 222, 223, 0.3)',
  },
  GOLD: {
    label: 'Gold Voucher',
    color: '#E8C078',
    gradient: 'linear-gradient(135deg, #F0D18B 0%, #B9852E 100%)',
    bgLight: 'rgba(232, 192, 120, 0.12)',
    border: 'rgba(232, 192, 120, 0.3)',
  },
  PLATINUM: {
    label: 'Platinum Voucher',
    color: '#A3BE8C',
    gradient: 'linear-gradient(135deg, #E5E9F0 0%, #A3BE8C 100%)',
    bgLight: 'rgba(163, 190, 140, 0.12)',
    border: 'rgba(163, 190, 140, 0.3)',
  },
};

export default function RedeemedCoupons({ onNavigate }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [isFirst, setIsFirst] = useState(true);
  const [isLast, setIsLast] = useState(true);
  const [selectedTier, setSelectedTier] = useState('ALL');
  const [toastMessage, setToastMessage] = useState('');
  const [toastOpen, setToastOpen] = useState(false);

  const isLoggedIn = !!(localStorage.getItem('token') || sessionStorage.getItem('token'));

  const fetchRedeemedCoupons = async (pageNumber = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/coupons/redeemed?page=${pageNumber}&size=10`);
      if (response && response.success && response.data) {
        setCoupons(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalElements(response.data.totalElements || 0);
        setIsFirst(response.data.first ?? true);
        setIsLast(response.data.last ?? true);
        setPage(response.data.number || 0);
      } else {
        throw new Error(response?.message || 'Failed to fetch redeemed coupons');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong while fetching redeemed coupons.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchRedeemedCoupons(page);
    } else {
      setLoading(false);
    }
  }, [page, isLoggedIn]);

  const handleCopyCode = (code) => {
    navigator.clipboard?.writeText(code).catch(() => {});
    setToastMessage(`Coupon Code ${code} copied to clipboard!`);
    setToastOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const filteredCoupons = selectedTier === 'ALL'
    ? coupons
    : coupons.filter((c) => c.tier?.toUpperCase() === selectedTier);

  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: '#0F2E2B',
          color: '#F5F1E6',
          py: { xs: 8, md: 12 },
          display: 'flex',
          alignItems: 'center',
          justify: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `
              radial-gradient(700px 500px at 85% -10%, rgba(212,162,76,0.12), transparent 60%),
              radial-gradient(600px 500px at -10% 80%, rgba(47,143,118,0.16), transparent 60%)
            `,
            zIndex: 0,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 5 },
              borderRadius: '28px',
              backgroundColor: 'rgba(21, 58, 53, 0.7)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(212, 162, 76, 0.3)',
              textAlign: 'center',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)',
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                backgroundColor: 'rgba(212, 162, 76, 0.15)',
                color: '#D4A24C',
                display: 'grid',
                placeItems: 'center',
                mx: 'auto',
                mb: 3,
                border: '1px solid rgba(212, 162, 76, 0.3)',
              }}
            >
              <LockIcon sx={{ fontSize: 36 }} />
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontFamily: '"Fraunces", serif',
                fontWeight: 700,
                color: '#F5F1E6',
                fontSize: { xs: '24px', sm: '28px' },
                mb: 1.5,
              }}
            >
              Redeemed Coupons Restricted
            </Typography>

            <Typography
              sx={{
                color: 'rgba(245, 241, 230, 0.7)',
                fontSize: '15px',
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Please log in to your account to view your redeemed cashback vouchers, track coupon usage history, and view redemption receipts.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => onNavigate && onNavigate('login')}
                startIcon={<PersonIcon />}
                sx={{
                  backgroundColor: '#D4A24C',
                  color: '#0F2E2B',
                  fontWeight: 700,
                  fontSize: '15px',
                  px: 4,
                  py: 1.4,
                  borderRadius: '14px',
                  '&:hover': {
                    backgroundColor: '#E8C078',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Sign In to View Redeemed Coupons
              </Button>

              <Button
                variant="outlined"
                onClick={() => onNavigate && onNavigate('signup')}
                sx={{
                  borderColor: 'rgba(245, 241, 230, 0.3)',
                  color: '#F5F1E6',
                  fontWeight: 600,
                  fontSize: '15px',
                  px: 3,
                  py: 1.4,
                  borderRadius: '14px',
                  '&:hover': {
                    borderColor: '#D4A24C',
                    color: '#D4A24C',
                    backgroundColor: 'rgba(212, 162, 76, 0.08)',
                  },
                }}
              >
                Create Account
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#0F2E2B',
        color: '#F5F1E6',
        py: { xs: 5, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(700px 500px at 85% -10%, rgba(212,162,76,0.12), transparent 60%),
            radial-gradient(600px 500px at -10% 80%, rgba(47,143,118,0.16), transparent 60%)
          `,
          zIndex: 0,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Navigation Tabs Header */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <Button
            onClick={() => onNavigate && onNavigate('my-coupon')}
            startIcon={<ConfirmationNumberIcon />}
            sx={{
              color: 'rgba(245, 241, 230, 0.6)',
              borderRadius: '12px',
              px: 2.5,
              py: 1,
              border: '1px solid rgba(245, 241, 230, 0.1)',
              '&:hover': { color: '#F5F1E6', borderColor: 'rgba(245, 241, 230, 0.3)' }
            }}
          >
            Active Coupons
          </Button>
          <Button
            startIcon={<HistoryIcon />}
            sx={{
              backgroundColor: '#E8C078',
              color: '#0F2E2B',
              fontWeight: 700,
              borderRadius: '12px',
              px: 2.5,
              py: 1,
              '&:hover': { backgroundColor: '#F0D18B' }
            }}
          >
            Redeemed Coupons
          </Button>
        </Box>

        {/* Page Title Header */}
        <Box sx={{ textAlign: 'left', mb: 5 }}>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '13px',
              color: '#E8C078',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              mb: 1,
            }}
          >
            Redeemed Rewards History
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 700,
              fontSize: { xs: '30px', md: '42px' },
              color: '#F5F1E6',
              mb: 1.5,
            }}
          >
            Redeemed Coupons
          </Typography>
          <Typography sx={{ color: 'rgba(245, 241, 230, 0.66)', fontSize: '16px', maxWidth: '650px' }}>
            Here are all your completed reward redemptions and claimed cashback vouchers.
          </Typography>
        </Box>

        {!isLoggedIn ? (
          /* Not Logged In State */
          <Paper
            elevation={0}
            sx={{
              background: 'rgba(21, 58, 53, 0.5)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(245, 241, 230, 0.12)',
              borderRadius: '24px',
              p: { xs: 4, md: 6 },
              textAlign: 'center',
              maxWidth: '540px',
              mx: 'auto',
              my: 6,
            }}
          >
            <LockIcon sx={{ fontSize: '48px', color: '#E8C078', mb: 2 }} />
            <Typography variant="h5" sx={{ fontFamily: '"Fraunces", serif', color: '#F5F1E6', fontWeight: 700, mb: 1.5 }}>
              Login Required
            </Typography>
            <Typography sx={{ color: 'rgba(245, 241, 230, 0.65)', fontSize: '15px', mb: 3 }}>
              Please log in to view your redeemed coupons history.
            </Typography>
            <Button
              variant="contained"
              onClick={() => onNavigate && onNavigate('login')}
              sx={{
                backgroundColor: '#E8C078',
                color: '#0F2E2B',
                fontWeight: 700,
                fontSize: '15px',
                px: 4,
                py: 1.2,
                borderRadius: '12px',
                '&:hover': { backgroundColor: '#F0D18B' },
              }}
            >
              Log In Now
            </Button>
          </Paper>
        ) : (
          <>
            {/* Filter Tabs */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 4, flexWrap: 'wrap' }}>
              {['ALL', 'GOLD', 'SILVER', 'BRONZE', 'PLATINUM'].map((tier) => (
                <Chip
                  key={tier}
                  label={tier === 'ALL' ? 'All Tiers' : `${tier} Tier`}
                  clickable
                  onClick={() => setSelectedTier(tier)}
                  sx={{
                    fontWeight: 700,
                    fontSize: '13px',
                    px: 1.5,
                    py: 2.2,
                    borderRadius: '12px',
                    backgroundColor: selectedTier === tier ? '#E8C078' : 'rgba(21, 58, 53, 0.6)',
                    color: selectedTier === tier ? '#0F2E2B' : '#F5F1E6',
                    border: '1px solid',
                    borderColor: selectedTier === tier ? '#E8C078' : 'rgba(245, 241, 230, 0.12)',
                    '&:hover': {
                      backgroundColor: selectedTier === tier ? '#F0D18B' : 'rgba(21, 58, 53, 0.9)',
                    },
                  }}
                />
              ))}
            </Box>

            {/* Content Loading / Error / Grid */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress sx={{ color: '#E8C078' }} />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ background: 'rgba(228,87,46,0.15)', color: '#FF7B54', border: '1px solid rgba(228,87,46,0.3)', my: 4 }}>
                {error}
              </Alert>
            ) : filteredCoupons.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  background: 'rgba(21, 58, 53, 0.4)',
                  backdropFilter: 'blur(12px)',
                  border: '1px dashed rgba(245, 241, 230, 0.15)',
                  borderRadius: '20px',
                  p: 6,
                  textAlign: 'center',
                  my: 4,
                }}
              >
                <EmojiEventsIcon sx={{ fontSize: 50, color: 'rgba(232, 192, 120, 0.4)', mb: 1.5 }} />
                <Typography variant="h6" sx={{ color: '#F5F1E6', fontWeight: 600, mb: 1 }}>
                  No Redeemed Coupons Found
                </Typography>
                <Typography sx={{ color: 'rgba(245, 241, 230, 0.6)', fontSize: '14px', maxWidth: '400px', mx: 'auto', mb: 3 }}>
                  {selectedTier === 'ALL'
                    ? "You haven't redeemed any coupon passes yet. Purchase a coupon card and scratch to unlock rewards!"
                    : `No redeemed coupons found for ${selectedTier} tier.`}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => onNavigate && onNavigate('my-coupon')}
                  sx={{
                    color: '#E8C078',
                    borderColor: '#E8C078',
                    fontWeight: 700,
                    borderRadius: '12px',
                    px: 3,
                    py: 1,
                    '&:hover': { borderColor: '#F0D18B', backgroundColor: 'rgba(232, 192, 120, 0.1)' }
                  }}
                >
                  View Active Purchased Coupons
                </Button>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                  gap: 3,
                  mb: 5,
                }}
              >
                {filteredCoupons.map((coupon, idx) => {
                  const tierKey = coupon.tier?.toUpperCase() || 'GOLD';
                  const tierInfo = TIER_CONFIG[tierKey] || TIER_CONFIG.GOLD;
                  const cashbackRupees = coupon.cashbackAmountPaise != null
                    ? (coupon.cashbackAmountPaise / 100).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                    : '0';

                  return (
                    <Paper
                      key={coupon.couponCode || idx}
                      elevation={0}
                      sx={{
                        background: 'rgba(21, 58, 53, 0.65)',
                        backdropFilter: 'blur(12px)',
                        border: `1px solid ${tierInfo.border}`,
                        borderRadius: '20px',
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 18px 40px rgba(0,0,0,0.45)`,
                          borderColor: tierInfo.color,
                        },
                      }}
                    >
                      {/* Left Stub */}
                      <Box
                        sx={{
                          background: tierInfo.gradient,
                          color: '#0F2E2B',
                          width: { xs: '100%', sm: '190px' },
                          p: 2.5,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          position: 'relative',
                          flexShrink: 0,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Chip
                            label={coupon.tier || 'REDEEMED'}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(15, 46, 43, 0.22)',
                              color: '#0F2E2B',
                              fontWeight: 800,
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '11px',
                              letterSpacing: '0.08em',
                            }}
                          />
                        </Box>

                        <Box sx={{ my: 1 }}>
                          <Typography sx={{ fontSize: '10.5px', textTransform: 'uppercase', fontWeight: 600, opacity: 0.75 }}>
                            Cashback Reward
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: '"Fraunces", serif',
                              fontSize: '28px',
                              fontWeight: 800,
                              lineHeight: 1.1,
                            }}
                          >
                            ₹{cashbackRupees}
                          </Typography>
                        </Box>

                        <Typography sx={{ fontSize: '11px', fontWeight: 700, opacity: 0.85, mt: 0.5 }}>
                          ✓ Redeemed Voucher
                        </Typography>
                      </Box>

                      {/* Perforated Vertical Line */}
                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'block' },
                          width: '1px',
                          borderRight: '2px dashed rgba(245, 241, 230, 0.18)',
                          my: 2,
                        }}
                      />

                      {/* Right Details */}
                      <Box
                        sx={{
                          p: 2.5,
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          gap: 1.5,
                        }}
                      >
                        {/* Status & Outcome Row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: '15px !important', color: '#2F8F76 !important' }} />}
                            label="REDEEMED"
                            sx={{
                              backgroundColor: 'rgba(47, 143, 118, 0.16)',
                              color: '#2F8F76',
                              border: '1px solid rgba(47, 143, 118, 0.3)',
                              fontWeight: 700,
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: '11.5px',
                            }}
                          />
                          {coupon.outcomeType && (
                            <Typography sx={{ color: '#E8C078', fontSize: '11.5px', fontWeight: 600, fontFamily: '"JetBrains Mono", monospace' }}>
                              {coupon.outcomeType.replace(/_/g, ' ')}
                            </Typography>
                          )}
                        </Box>

                        {/* Product Win & Cashback details */}
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(15, 46, 43, 0.5)',
                            border: '1px solid rgba(245, 241, 230, 0.06)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.6,
                          }}
                        >
                          {coupon.productItem && (
                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#FAF3E6' }}>
                              Product Win : <Box component="span" sx={{ color: '#E8C078' }}>{coupon.productItem}</Box>
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#FAF3E6' }}>
                            Cash Back : <Box component="span" sx={{ color: '#2F8F76', fontWeight: 800 }}>₹{cashbackRupees}</Box>
                          </Typography>
                        </Box>

                        {/* Coupon Code copy bar & Redeemed Date */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, pt: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon sx={{ color: '#E8C078', fontSize: '15px' }} />
                            <Typography sx={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)', fontWeight: 500 }}>
                              Redeemed On {formatDate(coupon.redeemedAt)}
                            </Typography>
                          </Box>

                          {coupon.couponCode && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: 'rgba(15, 46, 43, 0.7)', px: 1.5, py: 0.5, borderRadius: '8px', border: '1px solid rgba(245, 241, 230, 0.1)' }}>
                              <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '12px', fontWeight: 700, color: '#FAF3E6' }}>
                                {coupon.couponCode}
                              </Typography>
                              <Tooltip title="Copy Code">
                                <IconButton size="small" onClick={() => handleCopyCode(coupon.couponCode)} sx={{ color: '#E8C078', p: 0.2 }}>
                                  <ContentCopyIcon sx={{ fontSize: '14px' }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}

            {/* Pagination Controls */}
            {!loading && totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 4 }}>
                <IconButton
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  disabled={isFirst}
                  sx={{
                    color: '#F5F1E6',
                    border: '1px solid rgba(245, 241, 230, 0.15)',
                    '&.Mui-disabled': { color: 'rgba(245, 241, 230, 0.2)' },
                  }}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '14px', color: '#E8C078', fontWeight: 600 }}>
                  Page {page + 1} of {totalPages}
                </Typography>
                <IconButton
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={isLast}
                  sx={{
                    color: '#F5F1E6',
                    border: '1px solid rgba(245, 241, 230, 0.15)',
                    '&.Mui-disabled': { color: 'rgba(245, 241, 230, 0.2)' },
                  }}
                >
                  <NavigateNextIcon />
                </IconButton>
              </Box>
            )}
          </>
        )}

        {/* Snackbar Toast notification */}
        <Snackbar
          open={toastOpen}
          autoHideDuration={3000}
          onClose={() => setToastOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" sx={{ width: '100%', background: '#153A35', color: '#E8C078', border: '1px solid #E8C078' }}>
            {toastMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
